import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle, faPlug, faCalculator, faEye } from '@fortawesome/free-solid-svg-icons'
import { LoadingSpinner } from '../shared/components/ui/LoadingSpinner'
import { SENSOR_PORTS } from '../types/sensor'
import { api } from '../services'

interface SensorCalibrationProps {
  selectedDevice: any
  selectedSensorNumber: number
  onSensorSelect: (deviceId: number, sensorNumber: number) => void
}

interface CalibrationPoint {
  rawValue: number
  actualValue: number
}

export function SensorCalibration({ selectedDevice, selectedSensorNumber }: SensorCalibrationProps) {
  const [calibrationPoints, setCalibrationPoints] = useState<CalibrationPoint[]>([
    { rawValue: 0, actualValue: 0 },
    { rawValue: 100, actualValue: 100 },
  ])
  const [previewValue, setPreviewValue] = useState<number>(50)
  const [calculatedScale, setCalculatedScale] = useState<number>(1.0)
  const [calculatedOffset, setCalculatedOffset] = useState<number>(0.0)
  const [isLoading, setIsLoading] = useState(false)
  const [configData, setConfigData] = useState<any>(null)

  // Fetch sensor configuration when device or sensor changes
  useEffect(() => {
    if (!selectedDevice || !selectedSensorNumber) {
      return
    }

    const fetchData = async () => {
      setIsLoading(true)
      try {
        const configResponse = await api.getSensorConfig(selectedDevice.sigfoxId)
        setConfigData(configResponse.data)
      } catch (error) {
        console.error('Failed to fetch sensor configuration:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [selectedDevice, selectedSensorNumber])

  // Calculate calibration coefficients from points
  useEffect(() => {
    if (calibrationPoints.length >= 2) {
      const point1 = calibrationPoints[0]
      const point2 = calibrationPoints[1]

      if (point2.rawValue !== point1.rawValue) {
        const scale = (point2.actualValue - point1.actualValue) / (point2.rawValue - point1.rawValue)
        const offset = point1.actualValue - scale * point1.rawValue

        setCalculatedScale(scale)
        setCalculatedOffset(offset)
      }
    }
  }, [calibrationPoints])

  const addCalibrationPoint = () => {
    setCalibrationPoints([...calibrationPoints, { rawValue: 0, actualValue: 0 }])
  }

  const removeCalibrationPoint = (index: number) => {
    if (calibrationPoints.length > 2) {
      setCalibrationPoints(calibrationPoints.filter((_, i) => i !== index))
    }
  }

  const updateCalibrationPoint = (index: number, field: 'rawValue' | 'actualValue', value: number) => {
    const updated = [...calibrationPoints]
    updated[index][field] = value
    setCalibrationPoints(updated)
  }

  const convertValue = (rawValue: number): number => {
    return rawValue * calculatedScale + calculatedOffset
  }

  const applyCalibration = async () => {
    if (!selectedDevice || !selectedSensorNumber) return

    try {
      // For now, just show a toast. In a full implementation, this would call an API endpoint
      toast.info(
        `Calibration would be applied: Scale: ${calculatedScale.toFixed(6)}, Offset: ${calculatedOffset.toFixed(
          6
        )}. Note: Full calibration API not yet implemented.`,
        { duration: 15000 }
      )
    } catch (error) {
      console.error('Failed to apply calibration:', error)
      toast.error('Failed to apply calibration. Please try again.', { duration: 30000 })
    }
  }

  if (!selectedDevice || selectedSensorNumber === 0) {
    return (
      <div className="p-6 text-center">
        <FontAwesomeIcon icon={faPlug} className="h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Sensor</h3>
        <p className="text-gray-600">Choose a device and sensor from the Sensor List tab to configure calibration.</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const portName = SENSOR_PORTS[selectedSensorNumber as keyof typeof SENSOR_PORTS]
  const sensorKey = SENSOR_PORTS[selectedSensorNumber as keyof typeof SENSOR_PORTS]
  const currentConfig = configData?.[sensorKey]

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900">Sensor Calibration - Port {portName}</h3>
        <p className="text-sm text-gray-600">
          Device {selectedDevice?.deviceName || selectedDevice?.sigfoxId} • Sensor {selectedSensorNumber}
        </p>
      </div>

      {/* Current Configuration */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Current Configuration</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Scale Factor:</span>
            <span className="ml-2 font-mono">{currentConfig?.conversionScale?.toFixed(6) || '1.000000'}</span>
          </div>
          <div>
            <span className="text-gray-600">Offset:</span>
            <span className="ml-2 font-mono">{currentConfig?.conversionOffset?.toFixed(6) || '0.000000'}</span>
          </div>
        </div>
      </div>

      {/* Recent Readings - Placeholder for when readings API is implemented */}
      <div className="bg-blue-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-3">Recent Raw Readings</h4>
        <div className="text-sm text-blue-700">
          Recent readings will be displayed here when the readings API is implemented.
        </div>
      </div>

      {/* Calibration Points */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-medium text-gray-900">Calibration Points</h4>
          <button
            onClick={addCalibrationPoint}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Add Point
          </button>
        </div>

        <div className="space-y-3">
          {calibrationPoints.map((point, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div className="flex-1 grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Raw Value</label>
                  <input
                    type="number"
                    step="0.01"
                    value={point.rawValue}
                    onChange={(e) => updateCalibrationPoint(index, 'rawValue', Number(e.target.value))}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Actual Value</label>
                  <input
                    type="number"
                    step="0.01"
                    value={point.actualValue}
                    onChange={(e) => updateCalibrationPoint(index, 'actualValue', Number(e.target.value))}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </div>
              {calibrationPoints.length > 2 && (
                <button
                  onClick={() => removeCalibrationPoint(index)}
                  className="px-2 py-1 text-red-600 hover:text-red-800 text-sm"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Calculated Coefficients */}
      <div className="bg-green-50 rounded-lg p-6">
        <h4 className="text-sm font-medium text-green-900 mb-3 flex items-center">
          <FontAwesomeIcon icon={faCalculator} className="mr-2 h-4 w-4" />
          Calculated Coefficients
        </h4>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-green-700">Scale Factor</label>
            <div className="mt-1 font-mono text-lg text-green-900">{calculatedScale.toFixed(6)}</div>
          </div>
          <div>
            <label className="block text-xs font-medium text-green-700">Offset</label>
            <div className="mt-1 font-mono text-lg text-green-900">{calculatedOffset.toFixed(6)}</div>
          </div>
        </div>

        <div className="text-xs text-green-700 bg-green-100 rounded p-2">
          <strong>Formula:</strong> Calibrated Value = (Raw Value × {calculatedScale.toFixed(6)}) +{' '}
          {calculatedOffset.toFixed(6)}
        </div>
      </div>

      {/* Preview Conversion */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
          <FontAwesomeIcon icon={faEye} className="mr-2 h-4 w-4" />
          Preview Conversion
        </h4>
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Raw Value</label>
            <input
              type="number"
              step="0.01"
              value={previewValue}
              onChange={(e) => setPreviewValue(Number(e.target.value))}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
          <div className="text-gray-500 mt-6">→</div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Calibrated Value</label>
            <div className="mt-1 p-2 bg-white border border-gray-300 rounded-md font-mono text-sm">
              {convertValue(previewValue).toFixed(3)}
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3">
        <button
          onClick={applyCalibration}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Apply Calibration
        </button>
      </div>

      {/* Help */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
        <div className="flex">
          <FontAwesomeIcon icon={faExclamationTriangle} className="h-5 w-5 text-yellow-400 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Calibration Instructions</h3>
            <div className="mt-2 text-sm text-yellow-700">
              <ol className="list-decimal list-inside space-y-1">
                <li>Take readings at known reference values (e.g., 0% and 100% for moisture sensors)</li>
                <li>Enter the raw sensor readings and corresponding actual values</li>
                <li>The system will calculate linear calibration coefficients</li>
                <li>Preview the conversion with test values before applying</li>
                <li>Click "Apply Calibration" to save the new coefficients</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
